
/**
 * Module dependencies.
 */

import { recoverPassword } from 'src/api/auth/recover-password';
import { regexes } from 'src/core/utils/regexes';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import AuthBaseLayout from 'src/components/layout/auth-base-layout';
import Button from 'src/components/core/buttons/button';
import Input from 'src/components/core/forms/fields/input';
import Metatags from 'src/components/metatags';
import React, { ReactElement, useCallback } from 'react';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';

/**
 * `RecoverPassword` page.
 */

const RecoverPassword = (): ReactElement => {
  const { translate } = useTranslate();
  const { showMessage } = useSnackbar();
  const { isLoading: isLoadingRevover, mutate } = useMutation(recoverPassword);
  const {
    errors,
    formState,
    handleSubmit,
    register,
    reset
  } = useForm({ mode: 'onBlur' });

  const onSubmit = useCallback(({ email }) => {
    mutate({ email }, {
      onSuccess: () => {
        showMessage(
          translate('authentication:recoverPassword.successMessage', { email }),
          {
            appearance: 'default',
            duration: 4000
          }
        );

        reset();
      }
    });
  }, [mutate, reset, showMessage, translate]);

  const { isSubmitting } = formState;
  const isLoading = isLoadingRevover || isSubmitting;
  const description = translate('authentication:recoverPassword.description');
  const title = translate('authentication:recoverPassword.title');

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags
        description={description}
        pageTitle={title}
      />

      <AuthBaseLayout
        description={description}
        title={title}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Input
            disabled={isLoading}
            error={errors?.email}
            label={translate('forms:emailLabel')}
            name={'email'}
            placeholder={translate('forms:emailPlaceholder')}
            ref={register<any>({
              pattern: {
                message: translate('forms:emailPatternValidation'),
                value: regexes.email
              },
              required: {
                message: translate('forms:requiredField'),
                value: true
              }
            })}
          />

          <Button
            colorTheme={'primary'}
            isLoading={isLoading}
            type={'submit'}
            variant={'fill'}
          >
            {translate('authentication:recoverPassword.submitButton')}
          </Button>
        </form>
      </AuthBaseLayout>
    </>
  );
};

/**
 * Export `RecoverPassword` page.
 */

export default RecoverPassword;
